import { Database, Folder, Clock, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface Template {
  id: string;
  type: 'POSTGRESQL' | 'FILES';
  name: string;
  description: string;
  icon: 'database' | 'folder' | 'clock';
  scheduleType: 'CUSTOM';
  cronExpression: string;
  retentionDaily: number;
  retentionWeekly: number;
  retentionMonthly: number;
  retentionLast: number;
  alertGracePeriod: number;
  alertThrottle: number;
}

// Templates with multiple backups per day using cron expressions
// 2x per day: 12h and 18h (12 PM and 6 PM)
// 3x per day: 6h, 12h and 18h (6 AM, 12 PM and 6 PM)
export const TEMPLATES: Template[] = [
  {
    id: 'db-2x-day',
    type: 'POSTGRESQL',
    name: 'Database 2x/day',
    description: 'Backup database twice daily (12h and 18h)',
    icon: 'database',
    scheduleType: 'CUSTOM',
    cronExpression: '0 12,18 * * *',
    retentionDaily: 14,
    retentionWeekly: 8,
    retentionMonthly: 12,
    retentionLast: 0,
    alertGracePeriod: 10,
    alertThrottle: 60,
  },
  {
    id: 'files-2x-day',
    type: 'FILES',
    name: 'Files 2x/day',
    description: 'Backup files twice daily (12h and 18h) with VSS enabled',
    icon: 'folder',
    scheduleType: 'CUSTOM',
    cronExpression: '0 12,18 * * *',
    retentionDaily: 14,
    retentionWeekly: 8,
    retentionMonthly: 12,
    retentionLast: 0,
    alertGracePeriod: 10,
    alertThrottle: 60,
  },
  {
    id: 'db-3x-day',
    type: 'POSTGRESQL',
    name: 'Database 3x/day',
    description: 'Backup database three times daily (6h, 12h and 18h)',
    icon: 'database',
    scheduleType: 'CUSTOM',
    cronExpression: '0 6,12,18 * * *',
    retentionDaily: 21,
    retentionWeekly: 12,
    retentionMonthly: 12,
    retentionLast: 0,
    alertGracePeriod: 5,
    alertThrottle: 30,
  },
  {
    id: 'files-3x-day',
    type: 'FILES',
    name: 'Files 3x/day',
    description: 'Backup files three times daily (6h, 12h and 18h) with VSS enabled',
    icon: 'folder',
    scheduleType: 'CUSTOM',
    cronExpression: '0 6,12,18 * * *',
    retentionDaily: 21,
    retentionWeekly: 12,
    retentionMonthly: 12,
    retentionLast: 0,
    alertGracePeriod: 5,
    alertThrottle: 30,
  },
];

interface TemplateSelectionProps {
  selectedTemplate: Template | null;
  onSelect: (template: Template) => void;
  onNext: () => void;
}

const IconMap = {
  database: Database,
  folder: Folder,
  clock: Clock,
};

export function TemplateSelection({ selectedTemplate, onSelect, onNext }: TemplateSelectionProps) {
  const { t } = useTranslation('backups');

  const handleSelect = (template: Template) => {
    onSelect(template);
  };

  const getScheduleLabel = (template: Template) => {
    if (template.id.includes('2x')) return t('simpleWizard.template.schedule.twiceDaily');
    if (template.id.includes('3x')) return t('simpleWizard.template.schedule.threeTimesDaily');
    return t('simpleWizard.template.schedule.daily');
  };

  const dbTemplates = TEMPLATES.filter((t) => t.type === 'POSTGRESQL');
  const fileTemplates = TEMPLATES.filter((t) => t.type === 'FILES');

  const renderTemplateCard = (template: Template) => {
    const Icon = IconMap[template.icon];
    const isSelected = selectedTemplate?.id === template.id;

    return (
      <button
        type="button"
        key={template.id}
        onClick={() => handleSelect(template)}
        aria-pressed={isSelected}
        className={`relative w-full p-4 text-left rounded-xl border bg-surface-card-light dark:bg-surface-card-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-all ${
          isSelected
            ? 'ring-2 ring-brand-500 border-brand-600 dark:border-brand-400'
            : 'hover:border-border-strong-light dark:hover:border-border-strong-dark'
        }`}
      >
        <div className="flex items-center space-x-3">
          <div
            className={`p-2.5 rounded-lg ${
              isSelected
                ? 'bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400'
                : 'bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark'
            }`}
          >
            <Icon size={20} />
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                {t(`simpleWizard.template.items.${template.id}.name`)}
              </h4>
              {isSelected && (
                <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
            </div>

            <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-0.5">
              {t(`simpleWizard.template.items.${template.id}.description`)}
            </p>

            <span className="inline-block text-[10px] px-2 py-0.5 bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400 rounded-full font-medium mt-2">
              {getScheduleLabel(template)}
            </span>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
          {t('simpleWizard.template.title')}
        </h3>
        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
          {t('simpleWizard.template.description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Database Column */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b-2 border-accent-text-light/30 dark:border-accent-text-dark/30">
            <Database size={20} className="text-brand-600 dark:text-brand-400" />
            <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-wide text-sm">
              {t('simpleWizard.template.columns.database')}
            </h4>
            <span className="text-[10px] px-2 py-0.5 bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark rounded-full">
              {t('simpleWizard.template.columns.postgresql')}
            </span>
          </div>
          <div className="space-y-3">{dbTemplates.map(renderTemplateCard)}</div>
        </div>

        {/* Files Column */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b-2 border-state-ok-light/30 dark:border-state-ok-dark/30">
            <Folder size={20} className="text-state-ok-light dark:text-state-ok-dark" />
            <h4 className="font-black text-slate-900 dark:text-white uppercase tracking-wide text-sm">
              {t('simpleWizard.template.columns.files')}
            </h4>
            <span className="text-[10px] px-2 py-0.5 bg-surface-2-light dark:bg-surface-2-dark text-text-muted-light dark:text-text-muted-dark rounded-full">
              {t('simpleWizard.template.columns.vssEnabled')}
            </span>
          </div>
          <div className="space-y-3">{fileTemplates.map(renderTemplateCard)}</div>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedTemplate}
          className="px-6 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:bg-surface-border-light dark:disabled:bg-surface-border-dark disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors"
        >
          {t('simpleWizard.buttons.next')}
        </button>
      </div>
    </div>
  );
}
